/**
 * CompactGoalCard — Summary-only card used on Home preview and /goals list.
 * Entire card is a link to /goals/:id. No inline editing.
 */
import { Link } from "react-router-dom";
import { Goal } from "@/types/todo";
import { ChevronRight, CheckCircle2, MoreHorizontal, Trash2, Eye, MapPin, CalendarHeart, Sparkles } from "lucide-react";
import { getBucketState, isBucketItem, planDate, planLabel } from "@/lib/bucketPlan";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { format, isValid, parseISO } from "date-fns";
import { getCategoryVisual } from "@/utils/categoryImages";
import { Progress } from "@/components/ui/progress";
import { useTrailForGoal } from "@/hooks/useTrailForGoal";
import { Compass, Stamp } from "lucide-react";

interface Props {
  goal: Goal;
  /** When provided, shows a "⋯" menu with Delete. */
  onDelete?: (goal: Goal) => void;
}

export function CompactGoalCard({ goal, onDelete }: Props) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const visual = getCategoryVisual(goal.category);
  const Icon = visual.icon;

  const todos = goal.todos || [];
  const total = todos.length;
  const done = todos.filter((t) => t.completed).length;
  const progress = Math.round(goal.progress ?? 0);
  const isDone = !!goal.completedAt || progress >= 100;

  const dueLabel = (() => {
    if (!goal.dueDate) return null;
    const d = typeof goal.dueDate === "string" ? parseISO(goal.dueDate) : (goal.dueDate as Date);
    return isValid(d) ? format(d, "MMM d") : null;
  })();

  const bucket = isBucketItem(goal);
  const bucketState = bucket ? getBucketState(goal) : null;
  const pDate = bucket ? planDate(goal) : null;
  const { hint: trail, visited: stamped } = useTrailForGoal(goal);

  return (
    <div className="relative">
    <Link
      to={`/goals/${goal.id}`}
      aria-label={bucket ? `Open Bucket List experience ${goal.title}` : `Open goal ${goal.title}, ${progress}% complete`}
      className={`gd-surface gd-spring group relative block w-full overflow-hidden rounded-2xl px-4 py-4 transition-all hover:-translate-y-0.5 hover:shadow-gd-2 active:scale-[0.99] ${bucket ? "before:absolute before:inset-y-3 before:left-0 before:w-[3px] before:rounded-r-full before:bg-amber-500/70" : ""}`}
      style={{ touchAction: "manipulation" }}
    >
      <div className="flex items-start gap-3.5">
        <div
          className={`shrink-0 h-11 w-11 rounded-[14px] ${bucket ? "bg-amber-500/12 ring-1 ring-inset ring-amber-500/20 [&_svg]:!text-amber-600 dark:[&_svg]:!text-amber-400" : `${visual.chip} ring-1 ring-inset ring-foreground/5`} flex items-center justify-center`}
          aria-hidden="true"
        >
          {bucket ? (
            isDone ? <Sparkles className="h-5 w-5 text-white" /> : <MapPin className="h-5 w-5 text-white" />
          ) : isDone ? (
            <CheckCircle2 className={`h-6 w-6 ${visual.iconColor}`} />
          ) : (
            <Icon className={`h-5 w-5 ${visual.iconColor}`} />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="gd-kicker truncate">{bucket ? "Bucket List" : goal.category || "Goal"}</p>
          <div className="mt-1 flex items-center gap-2">
            <h3 className="font-outfit text-[15px] sm:text-base font-semibold leading-snug tracking-tight text-foreground truncate flex-1">
              {goal.title}
            </h3>
            {!bucket && (
              <span className="text-xs font-semibold tabular-nums text-muted-foreground shrink-0">
                {progress}%
              </span>
            )}
            {onDelete ? <span className="w-8 shrink-0" aria-hidden /> : <ChevronRight className="h-4 w-4 text-muted-foreground/60 shrink-0 group-hover:translate-x-0.5 transition-transform" />}
          </div>

          {bucket ? (
            // Experiences show a ladder state, never a % bar or "Due".
            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">

              {bucketState === "want" && (
                <span className="rounded-full bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-1 ring-inset ring-sky-500/15 px-2 py-0.5 font-semibold">Want to go</span>
              )}
              {bucketState === "planned" && pDate && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-800 dark:text-amber-300 ring-1 ring-inset ring-amber-500/20 px-2 py-0.5 font-semibold">
                  <CalendarHeart className="h-3 w-3" /> Planned · {planLabel(pDate)}
                </span>
              )}
              {bucketState === "missed" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-1 ring-inset ring-rose-500/15 px-2 py-0.5 font-semibold">
                  <Sparkles className="h-3 w-3" /> Did you go?
                </span>
              )}
              {bucketState === "done" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-inset ring-emerald-500/15 px-2 py-0.5 font-semibold">
                  <Sparkles className="h-3 w-3" /> Lived it
                </span>
              )}
              {trail && (
                <span
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted/60 ring-1 ring-inset ring-foreground/5 px-2 py-0.5 font-medium text-amber-900 dark:text-amber-100"
                  title={`Stop on ${trail.trailTitle}`}
                >
                  {stamped ? <Stamp className="h-3 w-3 shrink-0" /> : <Compass className="h-3 w-3 shrink-0" />}
                  <span className="truncate">{trail.joined ? (stamped ? "Stamped" : "On your trail") : "On a trail"}</span>
                </span>
              )}
            </div>
          ) : (
          <>
          <div className="mt-3">
            <Progress value={progress} className="h-1.5" />
          </div>

          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            {total > 0 && <span className="tabular-nums">{done}/{total} tasks</span>}
            {total > 0 && dueLabel && <span aria-hidden>·</span>}
            {dueLabel && <span>Due {dueLabel}</span>}
          </div>
          </>
          )}
        </div>
      </div>
    </Link>
    {onDelete && (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`More options for ${goal.title}`}
              style={{ touchAction: "manipulation" }}
              className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => navigate(`/goals/${goal.id}`)}>
              <Eye className="mr-2 h-4 w-4" /> Open goal
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setConfirmOpen(true)} className="text-destructive focus:text-destructive">
              <Trash2 className="mr-2 h-4 w-4" /> Delete goal
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this goal?</AlertDialogTitle>
              <AlertDialogDescription>"{goal.title}" will be removed. You can undo right after.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep it</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(goal)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    )}
    </div>
  );
}
